import React, { useEffect, useState } from 'react';
import { ArrowLeft, Code2, Crown, KeyRound, Lock } from 'lucide-react';
import { storageService } from '../services/storageService';
import { planService } from '../services/planService';
import type { PlanTier } from '../types/plan';
import type { DriveAuthSession } from '../types';

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const toSession = (auth: { accessToken: string; idToken?: string; userSub?: string; email?: string }): DriveAuthSession => ({
  accessToken: auth.accessToken,
  idToken: auth.idToken,
  userSub: auth.userSub,
  email: auth.email,
  expiresAt: Date.now() + 30 * 60 * 1000,
});

const DeveloperPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [tier, setTier] = useState<PlanTier>('free');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const auth = await storageService.getAuthSession();
        if (!active || !auth?.accessToken) {
          setAuthenticated(false);
          return;
        }
        setAuthenticated(true);
        const plan = await planService.getPlan(toSession(auth));
        if (active) setTier(plan.tier);
      } catch (err: any) {
        if (active) setError(err?.message || 'Unable to load plan details.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0A14] text-white flex items-center justify-center">
        <div className="text-white/40 text-sm" style={{ fontFamily: SANS }}>Loading developer portal...</div>
      </div>
    );
  }

  const hasDeveloperAccess = tier === 'pro' || tier === 'power';

  return (
    <div className="min-h-screen bg-[#0C0A14] text-white">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <a href="/" className="inline-flex items-center gap-2 text-white/45 hover:text-white/80 text-sm mb-8" style={{ fontFamily: SANS }}>
          <ArrowLeft size={14} />
          Back to Refract
        </a>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8">
          <div className="flex items-center gap-3 mb-4">
            <Code2 size={22} className="text-indigo-300/80" />
            <h1 className="text-4xl font-light" style={{ fontFamily: SERIF }}>Developer API</h1>
          </div>
          <p className="text-white/55 text-sm leading-7 mb-6" style={{ fontFamily: SANS }}>
            The Developer API is for scripted library workflows, external tools, and integrations.
            API key management is no longer available in the in-app player UI.
          </p>

          {!authenticated && (
            <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 mb-6">
              <p className="text-amber-200 text-sm flex items-center gap-2" style={{ fontFamily: SANS }}>
                <Lock size={14} />
                Sign in to view your eligibility and access status.
              </p>
              <a href="/" className="inline-block mt-3 text-sm px-4 py-2 rounded-xl bg-white text-black" style={{ fontFamily: SANS }}>
                Sign in
              </a>
            </div>
          )}

          {authenticated && !hasDeveloperAccess && (
            <div className="rounded-2xl border border-yellow-400/30 bg-yellow-400/10 p-4 mb-6">
              <p className="text-yellow-100 text-sm flex items-center gap-2" style={{ fontFamily: SANS }}>
                <Crown size={14} />
                Developer API access requires Pro or Power. You are currently on Free.
              </p>
              <a href="/" className="inline-block mt-3 text-sm px-4 py-2 rounded-xl bg-yellow-300 text-black" style={{ fontFamily: SANS }}>
                Upgrade from Plans
              </a>
            </div>
          )}

          {authenticated && hasDeveloperAccess && (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 mb-6">
              <p className="text-emerald-100 text-sm flex items-center gap-2" style={{ fontFamily: SANS }}>
                <KeyRound size={14} />
                Your plan supports Developer API access. Key issuing is managed by backend developer endpoints.
              </p>
            </div>
          )}

          <div className="space-y-4 text-sm text-white/60 leading-7" style={{ fontFamily: SANS }}>
            <p><strong className="text-white/85">What you can do:</strong> verify plans, run library automations, and build internal tooling around indexed media metadata.</p>
            <p><strong className="text-white/85">Authentication model:</strong> authenticated user session + developer key flow managed outside the player.</p>
            <p><strong className="text-white/85">Storage:</strong> API usage data is tracked server-side when available, with local fallback for resilience.</p>
          </div>

          {error && <p className="mt-5 text-rose-300/90 text-sm" style={{ fontFamily: SANS }}>{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default DeveloperPage;
